import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
    Users, School, MessageSquare, FileText,
    Download, Loader2, Shield, BarChart3,
    TrendingUp, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import GrantAccessPanel from '../components/GrantAccessPanel';
import EmailDiagnosticPanel from '../components/EmailDiagnosticPanel';
import WelcomeEmailsPanel from '../components/WelcomeEmailsPanel';
import SupportTicketsPanel from '../components/SupportTicketsPanel';
import DailyOpsPanel from '../components/DailyOpsPanel';
import VisitStatsPanel from '../components/VisitStatsPanel';
import ParvulariaReembolsoPanel from '../components/ParvulariaReembolsoPanel';
import ReportsPanel from '../components/ReportsPanel';
import BorradoresFunnelPanel from '../components/BorradoresFunnelPanel';
import CommercialFunnelPanel from '../components/CommercialFunnelPanel';
import EstablecimientoFunnelPanel from '../components/EstablecimientoFunnelPanel';
import EmailAutomationsPanel from '../components/EmailAutomationsPanel';
import ConvocatoriaPanel from '../components/ConvocatoriaPanel';
import CampaignsPanel from '../components/CampaignsPanel';
import BorradoresMonitorPanel from '../components/BorradoresMonitorPanel';
import PagosPanel from '../components/PagosPanel';

function StatCard({ title, value, icon: Icon, description, color }) {
    return (
        <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-slate-500">{title}</p>
                        <p className={cn('mt-1 text-3xl font-bold font-mono', color || 'text-slate-900')}>{value}</p>
                        {description && <p className="mt-1 text-xs text-slate-400">{description}</p>}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
                        <Icon className="h-6 w-6 text-slate-600" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [daily, setDaily] = useState([]);
    const [searches, setSearches] = useState([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, usersRes, dailyRes, searchesRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getUsers(),
                adminAPI.getUsersDaily(),
                adminAPI.getSearches(),
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data.users);
            setDaily(dailyRes.data.daily);
            setSearches(searchesRes.data.searches);
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('No tienes permisos de administrador');
                navigate('/dashboard');
            } else {
                toast.error('Error al cargar datos de administración');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await adminAPI.exportUsers();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'usuarios_radar_docente.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Archivo exportado correctamente');
        } catch {
            toast.error('Error al exportar');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex h-96 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            </Layout>
        );
    }

    const maxDaily = Math.max(...daily.map(d => d.count), 1);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <Shield className="h-6 w-6 text-slate-700" />
                            <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl" data-testid="admin-title">
                                Panel de Administración
                            </h1>
                        </div>
                        <p className="mt-1 text-slate-500">Vista exclusiva para administradores</p>
                    </div>
                    <Button
                        onClick={handleExport}
                        disabled={exporting}
                        className="gap-2"
                        data-testid="export-users-btn"
                    >
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Exportar Usuarios
                    </Button>
                </div>

                {/* Stats grid */}
                {stats && (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard title="Usuarios" value={stats.total_users} icon={Users} description="Registrados en la plataforma" />
                        <StatCard title="Escuelas" value={stats.schools_linked} icon={School} description="Establecimientos vinculados" />
                        <StatCard title="Consultas IA" value={stats.total_messages} icon={MessageSquare} description="Mensajes totales" />
                        <StatCard title="Borradores" value={stats.total_drafts} icon={FileText} description="Borradores creados" />
                    </div>
                )}

                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Users table - takes 2 cols */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Usuarios Registrados ({users.length})
                            </CardTitle>
                            <CardDescription>Todos los usuarios de la plataforma</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="users-table">
                                    <thead>
                                        <tr className="border-b border-slate-200 text-left">
                                            <th className="pb-3 font-semibold text-slate-500">Nombre</th>
                                            <th className="pb-3 font-semibold text-slate-500">Email</th>
                                            <th className="pb-3 font-semibold text-slate-500">Registro</th>
                                            <th className="pb-3 font-semibold text-slate-500 text-center" title="Preguntas del docente en el chat con la IA (turnos del usuario). No cuenta consultas de escuelas.">
                                                Mensajes chat IA
                                            </th>
                                            <th className="pb-3 font-semibold text-slate-500">Escuela</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr key={u.id} className="border-b border-slate-100 last:border-0">
                                                <td className="py-3 pr-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-slate-900">{u.nombre || 'Sin nombre'}</span>
                                                        {u.is_admin && (
                                                            <Badge className="bg-slate-900 text-white text-[10px] px-1.5 py-0">Admin</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-4 text-slate-600 text-xs">{u.email}</td>
                                                <td className="py-3 pr-4 text-slate-500 text-xs whitespace-nowrap">
                                                    {u.created_at ? new Date(u.created_at).toLocaleDateString('es-CL') : '-'}
                                                </td>
                                                <td className="py-3 pr-4 text-center" title={`${u.consultas_usuario ?? 0} preguntas · ${u.respuestas_ia ?? 0} respuestas IA · ${u.conversaciones} conversaciones`}>
                                                    <span className="font-mono font-bold text-slate-700">{u.consultas_usuario ?? u.consultas}</span>
                                                    <span className="ml-1 text-[10px] text-slate-400">/ {u.mensajes_totales ?? u.consultas}</span>
                                                </td>
                                                <td className="py-3 text-slate-500 text-xs max-w-[180px] truncate">
                                                    {u.escuela || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Right column */}
                    <div className="space-y-6">
                        {/* Popular searches */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg flex items-center gap-2">
                                    <Search className="h-5 w-5" />
                                    Indicadores más consultados
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {searches.length > 0 ? (
                                    <div className="space-y-3">
                                        {searches.map((s, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                                                    {i + 1}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm text-slate-700 truncate">{s.indicador}</p>
                                                </div>
                                                <Badge variant="outline" className="text-xs font-mono">{s.count}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4">Sin datos aún</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Daily chart */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5" />
                                    Usuarios nuevos (30 días)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-end gap-[3px] h-32" data-testid="daily-chart">
                                    {daily.map((d) => (
                                        <div
                                            key={d.date}
                                            className="flex-1 group relative"
                                            title={`${d.date}: ${d.count} usuario(s)`}
                                        >
                                            <div
                                                className={cn(
                                                    'w-full rounded-t transition-all',
                                                    d.count > 0 ? 'bg-slate-700 hover:bg-slate-900' : 'bg-slate-100'
                                                )}
                                                style={{
                                                    height: d.count > 0
                                                        ? `${Math.max((d.count / maxDaily) * 100, 8)}%`
                                                        : '4px',
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between mt-2 text-[10px] text-slate-400">
                                    <span>{daily[0]?.date?.slice(5)}</span>
                                    <span>Hoy</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* ───── Otorgar acceso promocional ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <GrantAccessPanel />
                </div>

                {/* ───── Correos de Bienvenida (envío MANUAL) ───── */}
                <div className="mt-8">
                    <WelcomeEmailsPanel />
                </div>

                {/* ───── 🔴 CONVOCATORIA 11 JUL 2026 · Envío único manual ───── */}
                <div className="mt-8">
                    <ConvocatoriaPanel />
                </div>

                {/* ───── KILL-SWITCHES Automatizaciones de correo (Default: OFF) ───── */}
                <div className="mt-8">
                    <EmailAutomationsPanel />
                </div>

                {/* ───── Campañas de Correo Early Access (1/8/14 jul) ───── */}
                <div className="mt-8">
                    <CampaignsPanel />
                </div>

                {/* ───── Bandeja de Soporte ───── */}
                <div className="mt-8">
                    <SupportTicketsPanel />
                </div>

                {/* ───── Diagnóstico de correos (Resend) ───── */}
                <div className="mt-8">
                    <EmailDiagnosticPanel />
                </div>

                {/* ───── Visitas a la portada (misma fuente que el contador público) ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <VisitStatsPanel />
                </div>

                {/* ───── Parvularia: reembolso evaluaciones sin rúbrica ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <ParvulariaReembolsoPanel />
                </div>

                {/* ───── Métricas Operativas Diarias ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <DailyOpsPanel />
                </div>

                {/* ───── Informes de Gestión (Diario / Semanal) ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <ReportsPanel />
                </div>

                {/* ───── 📝 Monitor de Borradores de Usuarios (solo superadmin) ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <BorradoresMonitorPanel currentEmail={user?.email} />
                </div>

                {/* ───── 💳 Gestión de Planes y Pagos (Fase 2 comercial) ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <PagosPanel />
                </div>

                {/* ───── 🎯 Embudo de Conversión Comercial (Fase 2 · Feb 2026) ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <CommercialFunnelPanel />
                </div>

                {/* ───── Embudo específico de Borradores ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <BorradoresFunnelPanel />
                </div>

                {/* ───── Embudo de Vinculación de Establecimiento ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <EstablecimientoFunnelPanel />
                </div>

                {/* ───── Analytics y Conversión ───── */}
                <div className="mt-10 pt-8 border-t border-slate-200">
                    <AnalyticsDashboard />
                </div>
            </div>
        </Layout>
    );
}
